import React, { useEffect } from 'react'
import {
  DialogTitle,
  DialogActions,
  DialogContent,
  Typography,
  CircularProgress,
  IconButton,
  Icon,
} from '@material-ui/core'
import ResponsiveDialog, {
  useDialogStyle,
} from 'app/components/dialog/ResponsiveDialog'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import { useMessageCredit } from './useMessageCreditAlert'
import { ENABLED_AUTOMATED_CALL } from 'app/constants/compose'
import { usePlace } from 'commons/usePlace'
import Loader from 'app/components/Loader'
import { FuseAnimate } from '@fuse'
import {
  TWITTER_VALUE,
  LINKEDIN_VALUE,
  APP_COUNT,
  EMAIL_COUNT,
  SMS_COUNT,
  AUTOMATED_CALL_COUNT,
} from 'app/constants/channel'

const useStyles = makeStyles(() => {
  return {
    dialogAction: {
      paddingLeft: '21px !important',
      paddingRight: '32px !important',
    },
    actionButton: {
      width: '90px',
      height: '40px',
    },
    dialogPaper: {
      '&.MuiDialog-paperWidthXs': {
        maxWidth: '480px',
      },
    },
    textLabel: {
      fontWeight: '400',
    },
  }
})

const MessageCreditAlert = ({ isOpen, setIsOpen, subscriberCount, state }) => {
  const classes = useDialogStyle()
  const styles = useStyles()
  const placeData = usePlace()
  const { formatMessage } = useIntl()
  const social = state.channels.channelIds.filter((id) =>
    [TWITTER_VALUE, LINKEDIN_VALUE].includes(id),
  )
  const socialCount = state.channels.pageIds.length + social.length

  const {
    state: { messaageRatio, isLoadingMessaageRatio },
    fetchMessageRatio,
  } = useMessageCredit()

  const multipleValuesBySubscriberCount = (value, credit) => {
    if (value) {
      const formular = [
        formatMessage(
          { id: 'message.credit.alert.dialog.per.subscriber' },
          { count: value },
        ),
        formatMessage(
          { id: 'message.credit.alert.dialog.credits' },
          { count: credit },
        ),
      ].join(' x ')
      return `(${formular}) ${value ? Number(value) * credit : 0}`
    }
    return '0'
  }

  const automatedCall = placeData[ENABLED_AUTOMATED_CALL]
    ? [
        {
          id: 'message-credit-automated-call',
          label: 'message.credit.alert.dialog.automateCall',
          countMessage: multipleValuesBySubscriberCount(
            subscriberCount[AUTOMATED_CALL_COUNT],
            messaageRatio.automatedCall,
          ),
          count:
            Number(subscriberCount[AUTOMATED_CALL_COUNT]) *
              Number(messaageRatio.automatedCall) || 0,
        },
      ]
    : []

  const statistic = [
    {
      id: 'message-credit-app',
      label: 'message.credit.alert.dialog.messageSpring',
      countMessage: subscriberCount[APP_COUNT] ?? '0',
      count: Number(subscriberCount[APP_COUNT]) || 0,
    },
    {
      id: 'message-credit-email',
      label: 'message.credit.alert.dialog.email',
      countMessage: multipleValuesBySubscriberCount(
        subscriberCount[EMAIL_COUNT],
        messaageRatio.email,
      ),
      count:
        Number(subscriberCount[EMAIL_COUNT]) * Number(messaageRatio.email) || 0,
    },
    {
      id: 'message-credit-sms',
      label: 'message.credit.alert.dialog.sms',
      countMessage: multipleValuesBySubscriberCount(
        subscriberCount[SMS_COUNT],
        messaageRatio.sms,
      ),
      count:
        Number(subscriberCount[SMS_COUNT]) * Number(messaageRatio.sms) || 0,
    },
    ...automatedCall,
    {
      id: 'message-credit-social',
      label: 'messsage.credit.alert.dialog.social',
      countMessage: multipleValuesBySubscriberCount(
        socialCount,
        messaageRatio.social,
      ),
      count: Number(socialCount) * Number(messaageRatio.social) || 0,
    },
  ]

  const closeDialog = () => {
    setIsOpen(false)
  }

  const firstMount = () => {
    if (isOpen) fetchMessageRatio(state)
  }

  useEffect(firstMount, [isOpen])

  return (
    <ResponsiveDialog
      open={isOpen}
      fullWidth={false}
      onClose={closeDialog}
      removeAllSpace
      className="text-blue-950 "
    >
      <IconButton
        aria-label="icon button close send a message"
        className="text-md rounded-none w-fit ml-auto"
        onClick={closeDialog}
      >
        <Icon className="text-20">close</Icon>
      </IconButton>
      <DialogTitle
        className={clsx(classes.dialogContent, 'pb-8 pt-2 text-blue-950')}
      >
        <div>
          <FormattedMessage id="message.credit.alert.dialog.header" />
        </div>
      </DialogTitle>

      <DialogContent
        className={clsx(classes.dialogContent, 'mb-10 text-blue-950')}
      >
        <Typography className={clsx('mb-10', styles.textLabel)}>
          <FormattedMessage id="message.credit.alert.dialog.note1" />
        </Typography>
        <Typography className={clsx('mb-10', styles.textLabel)}>
          <FormattedMessage id="message.credit.alert.dialog.note2" />
        </Typography>

        <div className="my-5">
          <div className="flex justify-between mb-4">
            <div>
              <Typography className={clsx('font-semibold', styles.textLabel)}>
                <FormattedMessage id="channels.label" />
              </Typography>
            </div>
            <div>
              <Typography className={clsx('font-semibold', styles.textLabel)}>
                <FormattedMessage id="planAndBilling.yourPlan.changePlan.plan.starterPack.notifications" />
              </Typography>
            </div>
          </div>
          {statistic.map((list) => {
            return (
              <div className="flex justify-between" key={list.id}>
                <Typography>
                  {<FormattedMessage id={`${list.label}`} />}
                </Typography>
                <Loader
                  isLoading={isLoadingMessaageRatio}
                  placeholder={
                    <FuseAnimate
                      enter={{ animation: 'fadeIn' }}
                      leave={{ animation: 'fadeOut' }}
                    >
                      <CircularProgress
                        className="mt-5"
                        color="secondary"
                        size={12}
                      />
                    </FuseAnimate>
                  }
                >
                  {list.countMessage}
                </Loader>
              </div>
            )
          })}
        </div>

        <div className="flex my-5 justify-between">
          <Typography className="font-semibold">
            <FormattedMessage id="message.credit.alert.dialog.total" />
          </Typography>
          <Loader
            isLoading={isLoadingMessaageRatio}
            placeholder={
              <FuseAnimate
                enter={{ animation: 'fadeIn' }}
                leave={{ animation: 'fadeOut' }}
              >
                <CircularProgress
                  className="mt-5"
                  color="secondary"
                  size={12}
                />
              </FuseAnimate>
            }
          >
            {statistic.reduce((n, { count }) => n + count || 0, 0)}
          </Loader>
        </div>

        <Typography className={clsx('my-5', styles.textLabel)}>
          <FormattedMessage id="message.credit.alert.dialog.label.reminder" />
        </Typography>
      </DialogContent>
    </ResponsiveDialog>
  )
}

export default MessageCreditAlert
